import * as TOML from 'toml';
import { defineStore } from 'pinia';
import { i18n } from 'src/boot/i18n';
import type { dynamicObj, ColumnDef } from 'src/pages/assets';
import helperUtil from 'src/utils/helperUtil';
import { CONSTANTS, isInvoice } from 'src/constants';
import { isEmptyString, isUndefinedOrNull} from 'src/utils/general';
import { documentPOApiService, purchaseOrderService, documentTypeApiService } from 'src/services';
import { NON_EXPENSE_DOC_TYPES } from 'src/pages/PurchaseOrders/constants';
import {
  useAmountFormatStore,
  useDocumentStore,
  useDocAttributesStore,
  useMasterDataLookupStore,
} from 'src/store/main';
import { notify } from 'src/components/notifications/notify';
import {
  getSavedColumns as getColumns,
  saveColumnOrder as saveColumns,
  getColumnFromServer,
} from 'src/utils/columnSettingsUtil';

export const usePoMatchStore = defineStore('po_match', {
  state: () => ({
    amount_format_store: useAmountFormatStore(),
    doc_store: useDocumentStore(),
    loading: false,
    po_preview: false,
    blockProcessing: false,
    total_net_amount: '',
    pending_amount: '',
    pending_amount_standard: '',
    matched_total_amount: '',
    charges_amount: '',
    amount_error: false,
    filterModel: {} as any,
    table_columns: {} as dynamicObj,
    toleranceModel: {} as dynamicObj,
    itemSpecificTolerancesMap: new Map(),
    unit_price_tolerance: 0,
    quantity_tolerance: 0,
    quantity_tolerance_type: '',
    unit_price_tolerance_type: '',
    supplier_options: [] as dynamicObj[],
    supplier_search: '',
    lineDataObj: {} as dynamicObj,
    PODataObj: {} as dynamicObj,
    po_connected_items: {} as dynamicObj,
    line_connected_items: {} as dynamicObj,
    auto_match_alert: false,
    is_view_readonly: false,

    enabledColumnsToSearch: [] as string[],
    po_line_id_for_match: [] as string[],
    po_line_for_match: [] as dynamicObj[],

    po_quantity_column: '',
    po_supplier_item_numbers: {} as dynamicObj,

    po_preview_columns: [] as dynamicObj[],
    po_columns: [] as dynamicObj[],
    is_multi_match: false,
    add_additional_columns: false,
    charges: {} as dynamicObj,
    isChargesModuleEnabled: false,
    additional_columns: [
      'delivery_number',
      'delivery_line_number',
    ] as string[],
    PO_TABLE_MAP: {} as Record<string, string>,
    PO_TABLE_MAP_REVERSE: {} as Record<string, string>,
  }),
  actions: {
    resetPoMatchStore() {
      setTimeout(() => {
        this.$reset();
      }, 0);
    },

    initFilterModel() {
      this.supplier_search = '';
      this.filterModel['q'] = '';
      this.filterModel['amounts_format'] = '';
      this.filterModel['supplier'] = '';
      this.filterModel['status'] = '';
      this.filterModel['order_status'] = '';
      this.filterModel['after_date'] = '';
      this.filterModel['before_date'] = '';
      this.filterModel['min_amount'] = '';
      this.filterModel['max_amount'] = '';
      this.filterModel['sort_by'] = '';
      this.filterModel['order'] = 'asc';
      this.filterModel['limit'] = 10;
      this.filterModel['skip'] = 0;
      this.enabledColumnsToSearch.forEach((column: string) => {
        this.filterModel[column] = '';
      });
    },
    hasPoMatchData() {
      if (
        !this.doc_store.record.po_multi_matched ||
        !Array.isArray(this.doc_store.record.po_multi_matched) ||
        this.doc_store.record.po_multi_matched.length === 0
      ) {
        return false;
      }
      return true;
    },

    setTotallineItem() {
      this.total_net_amount = '0';
      if (
        !this.doc_store.record.fields ||
        !Array.isArray(this.doc_store.record.fields) ||
        this.doc_store.record.fields.length === 0
      ) {
        return;
      }

      this.doc_store.record.fields.forEach((field: any, index: any) => {
        if (
          field.name !== 'total_net_amount' &&
          field.name !== 'total_net_amount_us'
        ) {
          return;
        }

        if (field.formatted_value) {
          this.total_net_amount = field.formatted_value;
        }
      });
    },

    async calculateMatchedTotal() {
      this.amount_error = false;

      if (NON_EXPENSE_DOC_TYPES.includes(this.doc_store.record.doc_type)) {
        return;
      }

      if (
        !this.doc_store.record.po_multi_matched ||
        this.doc_store.record.po_multi_matched.length === 0
      ) {
        this.matched_total_amount = await this.amount_format_store.formatValue(
          '0.00',
          'en_US',
          this.doc_store.amount_format_locale,
        );
        this.pending_amount = this.total_net_amount;
        this.charges_amount = this.matched_total_amount;
        return;
      }

      // maintain array of ids to calculate the charges of those items
      let lineIds: string[] = [];
      let matched_charges_amount_standard = 0;
      let matched_total_amount_standard = 0;
      this.is_multi_match = false;

      let result = {} as any;

      if (this.doc_store.record.use_lines_net_amount_for_matched_total) {
        result = this.getMatchedAmountFromLineItems();
      } else {
        result = this.getMatchedAmountFromPOMatching();
      }

      lineIds = result.lineIds;
      matched_total_amount_standard = result.matched_total_amount_standard;

      let total_net_amount_standard = '0.00';
      if (this.total_net_amount) {
        total_net_amount_standard = await this.amount_format_store.formatValue(
          this.total_net_amount,
          this.doc_store.amount_format_locale,
        );
      }

      const total_net_amount_float = parseFloat(total_net_amount_standard);
      let remaining_amount = total_net_amount_float - matched_total_amount_standard;

      if (this.isChargesModuleEnabled) {
        matched_charges_amount_standard = this.calculateCharges(lineIds);
        remaining_amount = +remaining_amount.toFixed(2) - matched_charges_amount_standard;
      }

      this.pending_amount_standard = +remaining_amount.toFixed(2);
      const matched_total_amount_standard_fixed = matched_total_amount_standard.toFixed(2);
      const matched_charges_amount_standard_fixed = matched_charges_amount_standard.toFixed(2);

      const values = [];
      values.push({
        key: this.pending_amount_standard,
        value: this.pending_amount_standard,
      });
      values.push({
        key: matched_total_amount_standard_fixed,
        value: matched_total_amount_standard_fixed,
      });
      values.push({
        key: matched_charges_amount_standard_fixed,
        value: matched_charges_amount_standard_fixed,
      });

      const formatted_values = await this.amount_format_store.formatMultiValues(
        values,
        'en_US',
        this.doc_store.amount_format_locale,
      );

      this.pending_amount = this.amount_format_store.searchValueByKey(
        this.pending_amount_standard,
        formatted_values,
      );

      this.matched_total_amount = this.amount_format_store.searchValueByKey(
        matched_total_amount_standard_fixed,
        formatted_values,
      );

      this.charges_amount = this.amount_format_store.searchValueByKey(
        matched_charges_amount_standard_fixed,
        formatted_values,
      );
    },
    getMatchedAmountFromPOMatching() {
      const lineIdsSet = new Set<string>();
      let matched_total_amount_standard = 0;

      for (const [ index,line_item] of this.doc_store.record.po_multi_matched.entries()) {

          if(line_item.hasOwnProperty('sub_lines') || line_item.hasOwnProperty('table_line_id')) {

            lineIdsSet.add(line_item.table_line_id);

            // FOR one line to many PO only calculate form table line
            const amount = this.getSublineAmount(line_item, true);

            if (!amount) {
              continue;
            }
            matched_total_amount_standard += parseFloat(amount);

          } else if (line_item.hasOwnProperty('table_lines')) {
          for (const [
            sub_index,
            table_line,
          ] of line_item.table_lines.entries()) {
            lineIdsSet.add(table_line.table_line_id);

            const amount = this.getSublineAmount(table_line, true);
            if (!amount) {
              continue;
            }

            matched_total_amount_standard += parseFloat(amount);
          }
        }
      }

      return { matched_total_amount_standard, lineIds: Array.from(lineIdsSet) };
    },

    getMatchedAmountFromLineItems() {
      const lineIdsSet = new Set<string>();
      let matched_total_amount_standard = 0;

      for (const [index, line_item] of this.doc_store.record.po_multi_matched.entries()) {
        if (line_item.hasOwnProperty('sub_lines') || line_item.hasOwnProperty('table_line_id')) {
          lineIdsSet.add(line_item.table_line_id);
        } else if (line_item.hasOwnProperty('table_lines')) {
          for (const [ sub_index,table_line] of line_item.table_lines.entries()) {
            lineIdsSet.add(table_line.table_line_id);
          }
        }
      }

      const lineIds = Array.from(lineIdsSet);

      for (const lineId of lineIds) {
        const net_amount = this.getlineColumnValue(lineId, 'NET_AMOUNT');

        if (!net_amount) {
          continue;
        }

        matched_total_amount_standard += Number(net_amount);
      }

      return { matched_total_amount_standard, lineIds };
    },

    getSublineAmount(line_item: any, use_discount = false) {
      try {
        const line_item_id = line_item.table_line_id;
        const quantity = this.getlineColumnValue(line_item_id, 'QUANTITY');
        const unit_price = this.getlineColumnValue(line_item_id, 'UNIT_PRICE');
        const unit_price_per =
          this.getlineColumnValue(line_item_id, 'UNIT_PRICE_PER') || 1;

        if (isEmptyString(quantity) || isEmptyString(unit_price)) {
          return '0.00';
        }

        return this.getCalculatedLineAmount(
          {
            quantity: quantity,
            unit_price: unit_price,
            unit_price_per: unit_price_per,
            table_line_id: line_item_id,
          },
          use_discount,
        );
      } catch (err) {
        logError(err);
      }
      return '0.00';
    },

    // getPOSublineAmount (line_item: any) {
    //   try {
    //     const po_line_id = line_item.po_line_id;
    //     const quantity = this.getPoQuantity(this.PODataObj[po_line_id]);
    //     const unit_price = this.getPoUnitPrice(this.PODataObj[po_line_id]);
    //     const unit_price_per = 1;

    //     if (!quantity || !unit_price) {
    //       return '0.00';
    //     }

    //     return this.getCalculatedLineAmount({
    //       quantity: quantity,
    //       unit_price: unit_price,
    //       unit_price_per: unit_price_per,
    //     }, false);

    //   } catch (err) {
    //     logError(err);
    //   }
    //   return '0.00';
    // },
    getPoUnitPrice(POItem: any) {
      let po_unit_price_per = POItem.unit_price_per
        ? Number(POItem.unit_price_per)
        : 1;

      if (isNaN(po_unit_price_per) || po_unit_price_per == 0) {
        po_unit_price_per = 1;
      }

      if (POItem.unit_price_calculated) {
        const po_unit_price = Number(POItem.unit_price_calculated);
        if (isNaN(po_unit_price) || po_unit_price == 0) {
          return 0;
        }
        return +(po_unit_price / +po_unit_price_per).toFixed(4);
      }

      const po_unit_price = POItem.hasOwnProperty('unit_price_value')
        ? Number(POItem.unit_price_value)
        : Number(POItem.unit_price);

      if (isNaN(po_unit_price) || po_unit_price == 0) {
        return 0;
      }

      const po_final_unit_price = po_unit_price / po_unit_price_per;
      return +po_final_unit_price;
    },
    getPoQuantity(POItem: any) {
      let quantity_column_name = 'quantity';
      const prefix = '_value';

      if (POItem.hasOwnProperty(this.po_quantity_column)) {
        quantity_column_name = this.po_quantity_column;
      }

      let po_quantity = POItem[quantity_column_name];
      if (POItem.hasOwnProperty(quantity_column_name + prefix)) {
        po_quantity = POItem[quantity_column_name + prefix];
      }

      if (
        isUndefinedOrNull(po_quantity) ||
        !helperUtil.methods.isNumeric(po_quantity)
      ) {
        po_quantity = 0;
      }

      return +po_quantity;
    },

    getCalculatedLineAmount (item: any, use_discount = false) {
      let line_amount = 0;
      try {
  
        if (!item.hasOwnProperty('quantity') || !item.hasOwnProperty('unit_price')) {
          return '0.00';
        }
  
        const quantity = Number(item['quantity']);
        let unit_price = Number(item['unit_price']);
        const unit_price_per = Number(item['unit_price_per']) || 1;
        if (use_discount) {
          // will only be executed if discount is on unit price
          unit_price = this.getDiscountedUnitPrice(item, unit_price);
        }
        unit_price = unit_price / unit_price_per;

        line_amount = quantity * unit_price;


      } catch (err) {
        logError(err);
      }
      return line_amount.toFixed(4);
    },

    // hasDiscount (line_item: any) {
    //   const settings_store = useSettingStore();
    //   if (!settings_store.isPOLineDiscountSet()) {
    //     return false;
    //   }

    //   const doc_tables_store = usePOLineItemsStore();
    //   return (
    //     this.lineDataObj.hasOwnProperty(line_item.table_line_id) &&
    //     this.lineDataObj[line_item.table_line_id].hasOwnProperty('columns') &&
    //     this.lineDataObj[line_item.table_line_id].columns.hasOwnProperty('DISCOUNT_PERCENT') &&
    //     !isUndefinedOrNull(this.lineDataObj[line_item.table_line_id].columns['DISCOUNT_PERCENT'].value) &&
    //     doc_tables_store.isColumnInTableColumns('DISCOUNT_PERCENT')
    //   ) as boolean;
    // },

    getDiscountedUnitPrice (line_item: any, final_unit_price: number) {
      try {
        // if (this.hasDiscount(line_item) === false) {
        //   return final_unit_price;
        // }

        let discount_amount = 0
        if(this.lineDataObj[line_item.table_line_id].columns['DISCOUNT_PER_UNIT'] && this.lineDataObj[line_item.table_line_id].columns['DISCOUNT_PER_UNIT'].value) {
          discount_amount = Number(this.lineDataObj[line_item.table_line_id].columns['DISCOUNT_PER_UNIT'].value);
        } else if(this.lineDataObj[line_item.table_line_id].columns['DISCOUNT_PERCENT'] && this.lineDataObj[line_item.table_line_id].columns['DISCOUNT_PERCENT'].value) {

          const discount_percent = Number(this.lineDataObj[line_item.table_line_id].columns['DISCOUNT_PERCENT'].value);
          discount_amount = final_unit_price * discount_percent / 100;

        } else if(this.lineDataObj[line_item.table_line_id].columns['DISCOUNT'] && this.lineDataObj[line_item.table_line_id].columns['DISCOUNT'].value) {
          const discount = Number(this.lineDataObj[line_item.table_line_id].columns['DISCOUNT'].value);
          const quantityCol = this.lineDataObj[line_item.table_line_id].columns['QUANTITY'];
          const quantity = (quantityCol && quantityCol.value) ? Number(quantityCol.value) : 1;

          return ((quantity * final_unit_price) - discount) / quantity;
        }

        return final_unit_price - discount_amount;
      } catch (error) {
        logError(error);
        return final_unit_price;
      }
    },


    getlineColumnValue(table_line_id: string, column_name: string) {
      try {
        if (
          this.lineDataObj.hasOwnProperty(table_line_id) &&
          this.lineDataObj[table_line_id].hasOwnProperty('columns') &&
          this.lineDataObj[table_line_id].columns.hasOwnProperty(column_name)
        ) {
           
          return this.lineDataObj[table_line_id].columns[column_name].value;
        }
      } catch (error) {
        logError(error, '[PO_MATCH] Failed to get line column value');
        throw error;
      }
      return '';
    },
    calculateCharges(lineIds: any) {
      let chargesAmount = this.calculateLineCharges(lineIds);

      chargesAmount += this.calculateHeaderCharges();

      return chargesAmount;
    },
    calculateLineCharges(lineIds: any) {
      let chargesAmount = 0;
      for (const lineId of lineIds) {
        const lineData = this.lineDataObj[lineId];

        // Skip if lineData is missing or costingElements is empty
        if (!lineData || !Array.isArray(lineData.costingElements) || lineData.costingElements.length === 0) {
            continue;
        }

        for (const costingElement of lineData.costingElements) {
          chargesAmount = chargesAmount + Number(this.getLineAmount(costingElement));
        }
      }
      return chargesAmount;
    },
    calculateHeaderCharges() {
      let chargesAmount = 0;
      if(!this.doc_store.record?.layout?.groups) {
        return chargesAmount;
      }

      const data: any = {};
      this.doc_store.record.fields.forEach((value: any) => {
        data[value.name] = value;
      });

      for (const group of this.doc_store.record.layout.groups) {
        for (const subgroup of group.rows) {
          for (const fieldDef of subgroup) {

            if(fieldDef.costing_element && !fieldDef.is_tax_field && data[fieldDef.field_name]?.value) {
              chargesAmount = chargesAmount + Number(data[fieldDef.field_name].value);
            }
          }
        }
      }
      
      return chargesAmount;
    },
    getLineAmount(item: any) {
      let amount = 0;

      if (item.hasOwnProperty('TOTAL_AMOUNT') && item.TOTAL_AMOUNT.value) {
        amount = item.TOTAL_AMOUNT.value;
      } else if (item.hasOwnProperty('NET_AMOUNT') && item.NET_AMOUNT.value) {
        amount = item.NET_AMOUNT.value;
      } else if (
        item.hasOwnProperty('UNIT_PRICE') ||
        item.hasOwnProperty('QUANTITY')
      ) {
        amount = item.UNIT_PRICE.value * item.QUANTITY.value;
      } else {
        notify.warning(
          i18n.t('PO_TABLE_COLUMN_MISSING'),
        );
      }

      return amount;
    },

    async getColumns(columns: string) {
      let visible_columns: any = {};
      try {
        const saved_columns = await getColumnFromServer(columns);
        if (!saved_columns || saved_columns == '') {
          visible_columns = '';
        } else {
          visible_columns = JSON.parse(saved_columns);
        }
      } catch (err) {
        visible_columns = '';
      }
      return visible_columns;
    },

    async getSavedColumns(po_columns: string) {
      return await getColumns({
        settingKey: po_columns,
        tableColumns: this.table_columns,
        defaultFilter: (col: any) => col.preview === true,
      });
    },

    async saveColumnOrder(settingKey: string, columns: any[]) {
      await saveColumns(settingKey, columns);
    },

    initToleranceModel() {
      this.toleranceModel = {
        item_number: '',
        supplier_item_number: '',
        quantity_tolerance: this.quantity_tolerance,
        unit_price_tolerance: this.unit_price_tolerance,
        unit_price_tolerance_type: this.unit_price_tolerance_type,
        quantity_tolerance_type: this.quantity_tolerance_type,
      };
    },

    buildPoLinesObj(po_items: dynamicObj[]) {
      this.PODataObj = {};
      if (!po_items) {
        return;
      }

      for (const [po_number, lines] of Object.entries(po_items)) {
        const po_lines = lines as dynamicObj[];
        for (const [index, po_line] of po_lines.entries()) {
          if (!this.PODataObj.hasOwnProperty(po_line.id)) {
            this.PODataObj[po_line.id] = po_line;
          }
        }
      }
    },

    // search utitlity
    prepareFilterRequestParams() {
      const params = {} as dynamicObj;
      for (const key of Object.keys(this.filterModel)) {
        if (this.filterModel[key]) {
          params[key] = this.filterModel[key];
        }
      }
      return params;
    },

    async setPoQuantityColumn () {

      let column_name = isInvoice(this.doc_store.record.doc_type) ? 'received_quantity' : 'quantity';

      try {
        const doc_attribute_store = useDocAttributesStore();
        const { model } = doc_attribute_store;

        if(model[CONSTANTS.PO_MATCH_SERVER_SIDE]) {
          const response = await documentTypeApiService.getActivePurchaseOrderRulesRevision(this.doc_store.record.doc_type);

          const poRules: any = TOML.parse(response.value);
          if (poRules?.matching_configuration?.po_effective_quantity_column) {
            column_name = poRules.matching_configuration.po_effective_quantity_column;
          }
        } else {
          const po_quantity_column = CONSTANTS.po_quantity_column;
          if (model.hasOwnProperty(po_quantity_column) && !isUndefinedOrNull(model[po_quantity_column]) ) {
            column_name = model[po_quantity_column];
          }
        }
      } catch (err) {
        logError(err);
        column_name = 'quantity';
      }

      this.po_quantity_column = column_name;
    },

    buildColumnsMap(
      list: Array<{ field: string | number }>,
      prefix = ''
    ): Record<string, { field: string | number }> {
      return list.reduce((acc, col) => {
        acc[`${prefix}${col.field}`] = col;
        return acc;
      }, {} as Record<string, ColumnDef>);
    },

    async getPOColumns(docType = '') {
      let response: any;
      try {
        const masterDataPO = 'purchase_order';
        const masterDataReceiveDelivery = 'receive_delivery';
        const masterDataReceiveDeliveryLines = 'receive_delivery_lines';

        const master_data_lookup = useMasterDataLookupStore();

        // get custom fields column names mappings
        const customColumPOList = await master_data_lookup.getColumnLabelPreference(masterDataPO);
        const customColumRDList = await master_data_lookup.getColumnLabelPreference(masterDataReceiveDelivery);
        const customColumRDLList = await master_data_lookup.getColumnLabelPreference(masterDataReceiveDeliveryLines);

        const poColumnsMap = this.buildColumnsMap(customColumPOList);
        const rdColumnsMap = this.buildColumnsMap(customColumRDList, 'rd_');
        const rdlColumnsMap = this.buildColumnsMap(customColumRDLList, 'rdl_');

        // merge All custom fields Mappings to handle in single loop
        const columnsMap: any = {
          ...poColumnsMap,
          ...rdColumnsMap,
          ...rdlColumnsMap,
        };

        const doc_store = useDocumentStore();
        const { record } = doc_store;

        const doc_type = docType
          ? docType
          : record && record.doc_type
            ? record.doc_type
            : 'PO_TABLE';
        const data = { doc_type: doc_type };
        response = await documentPOApiService.getPODisplayColumns(data);
        if (response.success === false) {
          throw new Error('[PO_MATCH] API response error');
        }

        // TODO why we are storing po_columns in extracted api result
        const po_columns = response.data;
        this.po_preview_columns = [] as dynamicObj[];

        po_columns.forEach((element: any, index: number) => {
          this.po_preview_columns.push(element);
          const colObj = {
            name: element.name,
            translation_key: columnsMap[element.name]
              ? columnsMap[element.name].label
              : element.translation_key,
            sortable: true,
            preview: element.preview,
            order: index,
          };

          this.table_columns[element.name] = colObj;
        });
        if (
          this.table_columns['purchase_order_number'] &&
          this.add_additional_columns
        ) {
          this.addAdditionalColumns();
        }
      } catch (err) {
        logError(err, '[PO_MATCH] Failed to get PO columns');
        throw new Error('[PO_MATCH] No columns for purchase order lines');
      }
    },
    addAdditionalColumns() {
      let order = 25;
      for (const [col_index, name] of Object.entries(this.additional_columns)) {
        //if we have already added the column then skip
        if (this.table_columns.find((col: any) => col.name == name)) {
          continue;
        }
        const item = {
          name: name,
          field: name,
          align: 'left',
          label: name,
          sortable: true,
          is_bold: false,
          order: order++,
        };
        this.table_columns.push(Object.assign({}, item));
      }
    },
    getPoTotalAmount(po_line_id: string) {
      let total_amount = '0.00';
      try {
        if (!this.PODataObj.hasOwnProperty(po_line_id)) {
          return total_amount;
        }

        const po_line = this.PODataObj[po_line_id];
        const column_name = 'total_amount';

        if (po_line.hasOwnProperty(column_name)) {
          total_amount = po_line[column_name];
        }

        const column_name_standard = 'total_amount_value';
        if (po_line.hasOwnProperty(column_name_standard)) {
          total_amount = po_line[column_name_standard];
        }
      } catch (err) {
        logError(err);
      }

      return total_amount;
    },

    getDifferenceAmount(line_item: dynamicObj) {
      const po_total_amount = this.getPoTotalAmount(line_item.po_line_id);
      const line_amount = this.getCalculatedLineAmount(line_item);

      const po_total_amount_float = parseFloat(po_total_amount);
      const line_amount_float = parseFloat(line_amount);
      let diff = po_total_amount_float - line_amount_float;
      diff = Number(Math.abs(diff).toFixed(4));
      return diff;
    },

    async getMatchedLines() {
      if (!this.doc_store.record.po_multi_matched) {
        return [];
      }
      const values = [];
      const matched_lines = [] as dynamicObj[];
      for (const [
        index,
        line_item,
      ] of this.doc_store.record.po_multi_matched.entries()) {
        const po_matched_item = JSON.parse(JSON.stringify(line_item));
        po_matched_item['amount_standard'] =
          this.getDifferenceAmount(line_item);
        matched_lines.push(po_matched_item);
        values.push({
          key: po_matched_item['amount_standard'],
          value: po_matched_item['amount_standard'],
        });
      }

      if (values.length > 0) {
        const formatted_values =
          await this.amount_format_store.formatMultiValues(
            values,
            'en_US',
            this.doc_store.amount_format_locale,
          );
        for (const [index, line_item] of matched_lines.entries()) {
          const diff = line_item['amount_standard'];
          line_item['amount'] = this.amount_format_store.searchValueByKey(
            diff,
            formatted_values,
          );
        }
      }

      return matched_lines;
    },
    prepareLinedictionary() {
      this.lineDataObj = {} as dynamicObj;
      const costingElementDictionary = {} as dynamicObj;

      const tables = this.doc_store.record.tables;

      if (
        !tables ||
        tables.length == 0 ||
        !tables[0].hasOwnProperty('rows') ||
        tables[0].rows.length == 0
      ) {
        return;
      }

      Array.from(tables[0].rows).forEach((row: any) => {
        if (row.is_deleted === false) {
          if (row.parent_id) {
            const newRow: dynamicObj = {};

            Array.from(row.columns).forEach((column: any) => {
              newRow[column.name] = column.formatted_value;
            });

            if (!costingElementDictionary.hasOwnProperty(row.parent_id)) {
              costingElementDictionary[row.parent_id] = [];
            }
            costingElementDictionary[row.parent_id].push(newRow);
          } else {
            // Clone the whole row ONCE (its columns come along with it) instead
            // of cloning the row and then re-cloning every column again. The old
            // code did a full JSON round-trip per row PLUS another per column, so
            // a document with many lines x many columns froze PO matching on
            // first render (DPT-255). One clone + reference reshuffling is enough.
            const newRow: dynamicObj = JSON.parse(JSON.stringify(row));

            // prepare row location list to get on indexes
            const rowLocations = {} as any;
            if (newRow.locations_list) {
              Array.from(newRow.locations_list).forEach((location: any) => {
                rowLocations['page_' + location.page] = location;
              });
            }
            newRow.locations_list = rowLocations;

            // columns array -> object keyed by column name (already cloned above)
            const columnsObj = {} as any;
            Array.from(newRow.columns).forEach((column: any) => {
              const columnLocations = {} as any;
              if (column.locations_list) {
                Array.from(column.locations_list).forEach((location: any) => {
                  columnLocations['page_' + location.page] = location;
                });
              }
              column.locations_list = columnLocations;
              columnsObj[column.name] = column;
            });
            newRow.columns = columnsObj;

            // Assign into reactive state only after the row is fully built, so
            // every nested property is in place behind Pinia's reactive proxy.
            this.lineDataObj[row.id] = newRow;
          }
        }
      });
    },

    async handleCharges (po_items: dynamicObj, forceRefresh = false) {
      // load charges
      // charges by po number and then item number
      //  take po_items key and check if it has value in charges
      const missing_charges_po = [] as string[];
      for (const [key, value] of Object.entries(po_items)) {
        if (this.charges.hasOwnProperty(key) && !forceRefresh) {
          continue;
        }
        this.charges[key] = {};
        missing_charges_po.push(key);
      }

      if (missing_charges_po.length == 0) {
        return;
      }

      try {
        const po_numbers = missing_charges_po.join(',');
        const response = await purchaseOrderService.getPoCharges({
          purchase_order_numbers: po_numbers,
        });
        if (response.success === false) {
          throw response;
        }

        for (const [key, value] of Object.entries(response.data)) {
          const charge_line = value as dynamicObj;
          if (!this.charges.hasOwnProperty(charge_line.purchase_order_number)) {
            this.charges[charge_line.purchase_order_number] = {};
          }

          if (
            !this.charges[charge_line.purchase_order_number].hasOwnProperty(
              charge_line.line_number,
            )
          ) {
            this.charges[charge_line.purchase_order_number][
              charge_line.line_number
            ] = [];
          }

          this.charges[charge_line.purchase_order_number][
            charge_line.line_number
          ].push(charge_line);
        }
      } catch (err) {
        logError(err);
      }
    },
    prepareLineMapping() {
      // Mapping from API fields to DB fields
      this.PO_TABLE_MAP = {
        line_number: 'POSITION',
        sub_line_number: 'SUB_POSITION',
        item_id: 'ITEM_NUMBER',
        supplier_item_id: 'SUPPLIER_ITEM_NUMBER',
        description: 'DESCRIPTION',
        purchase_order_number: 'PURCHASE_ORDER',
        quantity: 'QUANTITY',
        unit_code: 'UNIT_CODE',
        unit_price: 'UNIT_PRICE',
        unit_price_per: 'UNIT_PRICE_PER',
        net_amount: 'NET_AMOUNT',
        total_amount: 'TOTAL_AMOUNT',
        currency: 'CURRENCY',
        delivery_note_id: 'DELIVERY_NOTE',
        requested_ship_date: 'PROMISED_DELIVERY_DATE',
      };

      this.PO_TABLE_MAP_REVERSE = Object.fromEntries(
        Object.entries(this.PO_TABLE_MAP).map(([k, v]) => [v, k])
      );
    },

    // Helper methods for case-insensitive itemSpecificTolerancesMap operations
    setToleranceForItem(itemKey: string, tolerance: any) {
      if (!itemKey) return;
      const normalizedKey = itemKey.toLowerCase();
      this.itemSpecificTolerancesMap.set(normalizedKey, tolerance);
    },

    getToleranceForItem(itemKey: string): any {
      if (!itemKey) return undefined;
      const normalizedKey = itemKey.toLowerCase();
      return this.itemSpecificTolerancesMap.get(normalizedKey);
    },

    hasToleranceForItem(itemKey: string): boolean {
      if (!itemKey) return false;
      const normalizedKey = itemKey.toLowerCase();
      return this.itemSpecificTolerancesMap.has(normalizedKey);
    },
  },
});
